// src/components/BookingCalendarView.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  updateDoc,
  runTransaction,
  where,
  Timestamp,
  limit,
  getDocs
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import { useHorseAccess } from '../../hooks/useHorseAccess';
import { validateBooking } from '../../utils/bookingValidation';
import { createNotification } from '../../utils/notifications';
import { BookingCalendar } from './BookingCalendar';
import { BookingModal } from './BookingModal';
import { ErrorModal } from '../common/ErrorModal';
import { BlockedTimeModal } from './BlockedTimeModal';
import { ChevronLeft, Calendar, CalendarDays } from 'lucide-react';
import { subMinutes } from 'date-fns'; // **WICHTIGER IMPORT**

interface BookingCalendarViewProps {
  facilityId: string;
  onBack: () => void;
}

interface Booking {
  id: string;
  facilityId: string;
  horseId: string;
  userId: string;
  startTime: Date;
  endTime: Date;
  horseName: string;
  userDisplayName: string;
  horseOwnerId: string;
  status: string;
  confirmationDeadline?: Date; // Hinzugefügt
}

interface WaitlistEntry {
  id: string;
  facilityId: string;
  horseId: string;
  userId: string;
  startTime: Date;
  endTime: Date;
  createdAt: Date;
  status: string;
  userDisplayName: string;
  horseName?: string;
}

export const BookingCalendarView: React.FC<BookingCalendarViewProps> = ({
  facilityId,
  onBack
}) => {
  const { user } = useAuth();
  const { isManager } = useUserRole();
  const { canManageHorse } = useHorseAccess();
  const [facility, setFacility] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: Date; end: Date } | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [view, setView] = useState<'day' | 'week'>('day');
  const [isMobile, setIsMobile] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loadingBookings, setLoadingBookings] = useState<boolean>(false);
  const [loadingWaitlist, setLoadingWaitlist] = useState<boolean>(false);

  // Hilfsfunktion zum Vergleichen von Slots bis zur Minute
  const isSameSlot = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate() &&
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  };

  // Überwache die Fenstergröße für responsives Design
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setWindowWidth(width);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Passe die Ansicht basierend auf der Fenstergröße an
  useEffect(() => {
    if (windowWidth < 768 && view === 'week') {
      setView('day');
    }
  }, [windowWidth, view]);

  // Funktion zum Laden der Buchungen, memoisiert mit useCallback
  const loadBookings = useCallback(() => {
    setLoadingBookings(true);
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('facilityId', '==', facilityId)
    );

    const unsubscribe = onSnapshot(
      bookingsQuery,
      async (snapshot) => {
        console.log("Bookings snapshot erhalten:", snapshot.docs.length, "Dokumente");
        const bookingsData: Booking[] = await Promise.all(
          snapshot.docs.map(async docSnapshot => {
            const bookingData = docSnapshot.data();
            const horseDoc = await getDoc(doc(db, 'horses', bookingData.horseId));
            const horseData = horseDoc.exists() ? horseDoc.data() : null;
            const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
            const userData = userDoc.exists() ? userDoc.data() : null;

            return {
              id: docSnapshot.id,
              ...bookingData,
              startTime: bookingData.startTime.toDate(),
              endTime: bookingData.endTime.toDate(),
              horseName: horseData?.name || 'Unbekanntes Pferd',
              userDisplayName: userData?.name || userData?.email || 'Unbekannter Benutzer',
              horseOwnerId: horseData?.owner?.id || '',
              status: bookingData.status || 'pending',
              confirmationDeadline: bookingData.confirmationDeadline?.toDate(), // Konvertiert zu Date
            };
          })
        );
        setBookings(bookingsData);
        setLoadingBookings(false);
        console.log("Bookings gesetzt:", bookingsData);
      },
      (error) => {
        console.error("Fehler beim Laden der Buchungen:", error);
        setError("Fehler beim Laden der Buchungen.");
        setLoadingBookings(false);
      }
    );

    return unsubscribe;
  }, [facilityId]);

  // Funktion zum Laden der Warteliste, memoisiert mit useCallback
  const loadWaitlist = useCallback(() => {
    setLoadingWaitlist(true);
    const waitlistQuery = query(
      collection(db, 'waitlist'),
      where('facilityId', '==', facilityId)
    );

    const unsubscribe = onSnapshot(
      waitlistQuery,
      async (snapshot) => {
        console.log("Waitlist snapshot erhalten:", snapshot.docs.length, "Dokumente");
        const entries: WaitlistEntry[] = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          const userDoc = await getDoc(doc(db, 'users', data.userId));
          const userData = userDoc.exists() ? userDoc.data() : null;

          const horseDoc = await getDoc(doc(db, 'horses', data.horseId));
          const horseData = horseDoc.exists() ? horseDoc.data() : null;

          return {
            id: docSnapshot.id,
            ...data,
            startTime: data.startTime.toDate(),
            endTime: data.endTime.toDate(),
            createdAt: data.createdAt.toDate(),
            status: data.status,
            userDisplayName: userData?.name || userData?.email || 'Unbekannter Benutzer',
            horseName: horseData?.name || 'Unbekanntes Pferd'
          };
        }));
        setWaitlistEntries(entries);
        setLoadingWaitlist(false);
        console.log("Waitlist entries gesetzt:", entries);
      }
    );

    return unsubscribe;
  }, [facilityId]);

  // Funktion zum Überprüfen der Stornierungsberechtigungen, memoisiert mit useCallback
  const canCancelBooking = useCallback(async (booking: Booking): Promise<boolean> => {
    if (!user) {
      console.log('No user logged in');
      return false;
    }

    console.log('Checking cancel permissions:', {
      userId: user.uid,
      isManager,
      bookingUserId: booking.userId,
      horseId: booking.horseId
    });

    if (isManager) {
      console.log('User is manager - allowing cancel');
      return true;
    }
    
    if (booking.userId === user.uid) {
      console.log('User is booking creator - allowing cancel');
      return true;
    }

    try {
      const horseDoc = await getDoc(doc(db, 'horses', booking.horseId));
      if (!horseDoc.exists()) {
        console.log('Horse document not found');
        return false;
      }
      
      const horseData = horseDoc.data();
      console.log('Horse data:', {
        ownerId: horseData?.owner?.id,
        currentUserId: user.uid,
        coRiders: horseData?.coRiders
      });

      // Besitzer darf immer stornieren
      if (horseData?.owner?.id === user.uid) {
        console.log('User is horse owner - allowing cancel');
        return true;
      }
      
      // Co-Riders dürfen stornieren, wenn sie approved sind
      const isApprovedCoRider = horseData?.coRiders?.[user.uid]?.status === 'approved';
      if (isApprovedCoRider) {
        console.log('User is approved co-rider - allowing cancel');
        return true;
      }
      
      console.log('No permission found for cancellation');
      return false;
    } catch (error) {
      console.error('Error checking booking permissions:', error);
      return false;
    }
  }, [user, isManager]);

  // useEffect zum Laden der Buchungen beim Mounten und bei Änderung der facilityId
  useEffect(() => {
    console.log("useEffect: Lade bookings für facilityId:", facilityId);
    const unsubscribe = loadBookings();

    return () => unsubscribe();
  }, [loadBookings, facilityId]);

  // useEffect zum Laden der Warteliste beim Mounten und bei Änderung der facilityId
  useEffect(() => {
    console.log("useEffect: Lade waitlist entries für facilityId:", facilityId);
    const unsubscribe = loadWaitlist();

    return () => unsubscribe();
  }, [loadWaitlist, facilityId]);

  // useEffect zum Laden der Facility-Daten
  useEffect(() => {
    console.log("useEffect: Lade facility Daten für facilityId:", facilityId);
    const unsubscribe = onSnapshot(
      doc(db, 'facilities', facilityId),
      (snapshot) => {
        if (snapshot.exists()) {
          const facilityData = snapshot.data();
          console.log('Loaded facility data:', facilityData);

          setFacility({
            id: facilityId,
            ...facilityData,
            blockedTimes: facilityData.blockedTimes?.map((block: any) => ({
              ...block,
              start: block.start.toDate(),
              end: block.end.toDate(),
              recurrenceEnd: block.recurrenceEnd?.toDate()
            })) || []
          });
        } else {
          console.log('Facility not found:', facilityId);
          setError('Einrichtung nicht gefunden.');
        }
      },
      (error) => {
        console.error("Fehler beim Laden der Facility-Daten:", error);
        setError("Fehler beim Laden der Einrichtung.");
      }
    );

    return () => unsubscribe();
  }, [facilityId]);

  // useEffect zum Öffnen des Buchungsmodals, wenn ein Zeit-Slot ausgewählt wird
  useEffect(() => {
    if (selectedTimeSlot) {
      console.log("useEffect: Öffne Buchungsmodal für ausgewählten Zeit-Slot:", selectedTimeSlot);
      setIsBookingModalOpen(true);
    } else {
      setIsBookingModalOpen(false);
    }
  }, [selectedTimeSlot]);

  // Handle für die Buchung, memoisiert mit useCallback
  const handleBooking = useCallback(async (horseId: string) => {
    if (!selectedTimeSlot || !facility || !user) return;

    try {
      await validateBooking(
        facilityId,
        horseId,
        selectedTimeSlot.start,
        selectedTimeSlot.end,
        facility.bookingSettings,
        isManager
      );

      const bookingData = {
        facilityId,
        horseId,
        userId: user.uid,
        startTime: Timestamp.fromDate(selectedTimeSlot.start),
        endTime: Timestamp.fromDate(selectedTimeSlot.end),
        createdAt: Timestamp.now(),
        status: facility.bookingSettings.autoConfirm ? 'confirmed' : 'pending',
        confirmationDeadline: facility.bookingSettings.minBookingNotice
          ? Timestamp.fromDate(subMinutes(selectedTimeSlot.start, facility.bookingSettings.minBookingNotice))
          : undefined,
      };

      const bookingRef = await addDoc(collection(db, 'bookings'), bookingData);
      // setSelectedTimeSlot(null); // Entfernen
      // setIsBookingModalOpen(false); // Entfernen

      const horseDoc = await getDoc(doc(db, 'horses', horseId));
      const horseData = horseDoc.exists() ? horseDoc.data() : null;

      if (horseData?.owner?.id !== user.uid) {
        await createNotification({
          recipientId: horseData.owner.id,
          priority: 'high',
          metadata: {
            type: 'BOOKING_CREATED',
            bookingId: bookingRef.id,
            facilityName: facility.name,
            horseName: horseData.name || 'Unbekanntes Pferd',
            startTime: selectedTimeSlot.start
          }
        });
      }

      if (horseData?.coRiders) {
        await Promise.all(
          Object.entries(horseData.coRiders)
            .filter(([_, coRider]: [string, any]) => coRider.status === 'approved')
            .map(([coRiderId]) => 
              createNotification({
                recipientId: coRiderId,
                priority: 'medium',
                metadata: {
                  type: 'BOOKING_CREATED',
                  bookingId: bookingRef.id,
                  facilityName: facility.name,
                  horseName: horseData.name || 'Unbekanntes Pferd',
                  startTime: selectedTimeSlot.start
                }
              })
            )
        );
      }

      setSuccess('Buchung erfolgreich erstellt.'); // Erfolgsmeldung hinzufügen
    } catch (error: any) {
      console.error('Error creating booking:', error);
      setError(error.message || 'Die Buchung konnte nicht erstellt werden.');
    }
  }, [selectedTimeSlot, facility, user, facilityId, isManager]);

  // Handle für die Bestätigung einer Buchung, memoisiert mit useCallback
 const handleConfirmBooking = useCallback(async (booking: Booking) => {
    console.log('handleConfirmBooking aufgerufen für Buchung:', booking.id);

    // Nur Status 'pending-confirmation' kann bestätigt werden
    if (booking.status !== 'pending-confirmation') {
      setError('Diese Buchung kann nicht bestätigt werden.');
      return;
    }

    // Berechtigungsprüfung: Nur der ursprüngliche Wartelistenbenutzer darf bestätigen
    if (!isManager && booking.userId !== user?.uid) {
      console.log('Berechtigung verweigert für Buchung:', booking.id);
      setError('Nur der ursprüngliche Wartelistenplatz-Inhaber kann diese Buchung bestätigen.');
      return;
    }

    // Prüfen ob es noch andere aktive Buchungen für dieses Pferd im gleichen Slot gibt
    const hasOtherBooking = bookings.some(otherBooking => 
      otherBooking.horseId === booking.horseId && 
      otherBooking.id !== booking.id && 
      isSameSlot(otherBooking.startTime, booking.startTime) &&
      ['confirmed', 'pending'].includes(otherBooking.status)
    );

    if (hasOtherBooking) {
      setError('Die ursprüngliche Buchung wurde noch nicht storniert.');
      return;
    }

    try {
      console.log('Buchung wird bestätigt:', booking.id);
      await updateDoc(doc(db, 'bookings', booking.id), {
        status: 'confirmed',
        confirmationDeadline: null // Frist entfernen
      });

      // Benachrichtigung senden
      await createNotification({
        recipientId: booking.userId,
        priority: 'high',
        metadata: {
          type: 'BOOKING_CONFIRMED',
          bookingId: booking.id,
          facilityName: facility.name,
          horseName: booking.horseName,
          startTime: booking.startTime
        }
      });

      console.log('Buchung erfolgreich bestätigt:', booking.id);
      setSuccess('Buchung erfolgreich bestätigt.');
      setError(null);
    } catch (error: any) {
      console.error('Error confirming booking:', error);
      setError(error.message || 'Die Buchung konnte nicht bestätigt werden.');
    }
  }, [isManager, facility, user, bookings, isSameSlot]);

  // Handle für das Stornieren einer Buchung, memoisiert mit useCallback
  const handleCancelBooking = useCallback(async (booking: Booking) => {
    try {
      console.log('Attempting to cancel booking:', {
        bookingId: booking.id,
        horseId: booking.horseId,
        userId: user?.uid,
        horseOwnerId: booking.horseOwnerId,
        isManager,
        booking: booking
      });

      const hasPermission = await canCancelBooking(booking);
      console.log('Cancel permission check result:', hasPermission);

      if (!hasPermission) {
        setError('Sie haben keine Berechtigung, diese Buchung zu stornieren.');
        return;
      }

      // Lösche die Buchung
      await deleteDoc(doc(db, 'bookings', booking.id));

      // Benachrichtigung senden
      await createNotification({
        recipientId: booking.userId,
        priority: 'high',
        metadata: {
          type: 'BOOKING_CANCELLED',
          bookingId: booking.id,
          facilityName: facility.name,
          horseName: booking.horseName,
          startTime: booking.startTime
        }
      });

      // Warteliste überprüfen und gegebenenfalls aktualisieren
      if (waitlistEntries.length > 0) {
        const nextInLine = waitlistEntries[0];
        const minBookingNotice = facility.bookingSettings.minBookingNotice; // in Minuten

        // Bestätigungsfrist berechnen
        const confirmationDeadline = subMinutes(booking.startTime, minBookingNotice);
        const now = new Date();

        console.log(`MinBookingNotice: ${minBookingNotice} Minuten`);
        console.log(`ConfirmationDeadline: ${confirmationDeadline}`);
        console.log(`Now: ${now}`);

        if (now > confirmationDeadline) {
          setError('Die Frist zur Bestätigung ist bereits abgelaufen.');
          return;
        }

        // Erstelle eine neue Buchung mit Status 'pending-confirmation'
        const newBookingData = {
          facilityId: facility.id,
          horseId: nextInLine.horseId,
          userId: nextInLine.userId,
          startTime: Timestamp.fromDate(nextInLine.startTime),
          endTime: Timestamp.fromDate(nextInLine.endTime),
          createdAt: Timestamp.fromDate(new Date()),
          status: 'pending-confirmation',
          confirmationDeadline: Timestamp.fromDate(confirmationDeadline)
        };

        const bookingRef = await addDoc(collection(db, 'bookings'), newBookingData);
        console.log('Buchung als pending-confirmation erstellt:', newBookingData);

        // Lösche den Wartelisten-Eintrag
        await deleteDoc(doc(db, 'waitlist', nextInLine.id));

        // Benachrichtigung an den nächsten Wartelisten-Eintrag senden
        await createNotification({
          recipientId: nextInLine.userId,
          priority: 'high',
          metadata: {
            type: 'BOOKING_PENDING_CONFIRMATION',
            bookingId: bookingRef.id,
            facilityName: facility.name,
            horseName: nextInLine.horseName || 'Unbekanntes Pferd',
            startTime: nextInLine.startTime
          }
        });

        setSuccess('Buchung erfolgreich storniert und Wartelistenplatz übernommen.');
      } else {
        setSuccess('Buchung erfolgreich storniert.');
      }

    } catch (error: any) {
      console.error('Error canceling booking:', error);
      setError(error.message || 'Die Buchung konnte nicht storniert werden.');
    }
  }, [canCancelBooking, facility, user, waitlistEntries]);

  // Handle für das Stornieren eines Wartelisten-Eintrags, memoisiert mit useCallback
  const handleCancelWaitlistEntry = useCallback(async (entryId: string) => {
    try {
      await deleteDoc(doc(db, 'waitlist', entryId));

      // **Pferde-Liste aktualisieren**
      setWaitlistEntries(prevEntries => prevEntries.filter(entry => entry.id !== entryId));

      // **Erfolgsmeldung aktualisieren**
      setSuccess('Wartelistenplatz wurde erfolgreich storniert.');
    } catch (error: any) {
      console.error('Error canceling waitlist entry:', error);
      setError(error.message || 'Wartelistenplatz konnte nicht storniert werden.');
    }
  }, []);

  // Handle für das Hinzufügen zur Warteliste, memoisiert mit useCallback
  const handleJoinWaitlist = useCallback(async (horseId: string) => {
    if (!selectedTimeSlot || !facility || !user) return;

    // Überprüfe, ob bereits ein Wartelistenplatz existiert
    const existingWaitlist = waitlistEntries.find(entry => 
      entry.horseId === horseId && 
      isSameSlot(entry.startTime, selectedTimeSlot.start)
    );

    if (existingWaitlist) {
      setError('Es existiert bereits ein Wartelistenplatz für dieses Pferd in diesem Slot.');
      return;
    }

    // Überprüfe, ob die Slot-Kapazität erreicht ist
    const bookingsForSlot = bookings.filter(booking => 
      isSameSlot(booking.startTime, selectedTimeSlot.start)
    );

    if (bookingsForSlot.length < facility.capacity) {
      setError('Dieser Slot ist noch verfügbar. Bitte buchen Sie stattdessen.');
      return;
    }

    try {
      await runTransaction(db, async (transaction) => {
        const waitlistRef = collection(db, 'waitlist');
        const q = query(
          waitlistRef,
          where('facilityId', '==', facility.id),
          where('horseId', '==', horseId),
          where('startTime', '==', Timestamp.fromDate(selectedTimeSlot.start)),
          limit(1)
        );

        const waitlistSnap = await getDocs(q);

        if (!waitlistSnap.empty) {
          throw new Error('Es existiert bereits ein Wartelistenplatz für dieses Pferd in diesem Slot.');
        }

        const newWaitlistEntry = {
          facilityId: facility.id,
          horseId: horseId,
          userId: user.uid,
          startTime: Timestamp.fromDate(selectedTimeSlot.start),
          endTime: Timestamp.fromDate(selectedTimeSlot.end),
          createdAt: Timestamp.now(),
          status: 'pending',
          userDisplayName: user.displayName || 'Unbekannt',
          horseName: bookings.find(b => b.horseId === horseId)?.horseName || 'Unbekannt'
        };

        await addDoc(waitlistRef, newWaitlistEntry);
      });

      setSuccess('Sie wurden erfolgreich zur Warteliste hinzugefügt.');
      // setIsBookingModalOpen(false); // Entfernen
    } catch (error: any) {
      console.error('Error joining waitlist:', error);
      setError(error.message || 'Wartelistenplatz konnte nicht erstellt werden.');
    }
  }, [selectedTimeSlot, facility, user, waitlistEntries, bookings, isSameSlot]);

  // Initialisiere die Daten, sobald die Komponente gemountet wird
  // Dieser Schritt ist optional, da die useEffect-Hooks bereits die Daten laden
  // Sie können diesen Abschnitt entfernen, wenn er redundant ist
  useEffect(() => {
    loadBookings();
    loadWaitlist();
  }, [loadBookings, loadWaitlist]);

  if (!facility) return null;

  return (
    <div className="space-y-4">
      {/* Header mit Zurück-Button und Facility-Name */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          onClick={onBack}
          className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Zurück
        </button>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{facility.name}</h2>
      </div>

      {/* Fehler- und Erfolgsmeldungen */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 dark:bg-green-900/50 p-4">
          <p className="text-sm text-green-700 dark:text-green-200">{success}</p>
        </div>
      )}

      {/* Buchungskalender */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setView('day')}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm ${
                view === 'day'
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Tagesansicht"
            >
              <Calendar className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Tag</span>
            </button>

            {!isMobile && (
              <button
                onClick={() => setView('week')}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm ${
                  view === 'week'
                    ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title="Wochenansicht"
              >
                <CalendarDays className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">Woche</span>
              </button>
            )}
          </div>
        </div>

        <div className="relative w-full">
          <BookingCalendar
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            facility={facility}
            existingBookings={bookings}
            waitlistEntries={waitlistEntries} // Hinzugefügt
            onTimeSlotSelect={setSelectedTimeSlot}
            selectedTimeSlot={selectedTimeSlot}
            view={view}
            onCancelBooking={handleCancelBooking}
            onConfirmBooking={handleConfirmBooking}
            isManager={isManager}
            canCancelBooking={canCancelBooking}
          />
        </div>
      </div>

      {/* Buchungsmodal */}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setSelectedTimeSlot(null);
          setError(null);
          setSuccess(null);
        }}
        onBook={handleBooking}
        onConfirmBooking={handleConfirmBooking}
        onCancelBooking={handleCancelBooking}
        onJoinWaitlist={handleJoinWaitlist} // Hinzugefügt
        selectedSlot={selectedTimeSlot}
        isManager={isManager}
        facility={facility}
        canCancelBooking={canCancelBooking}
        existingBookings={bookings.filter(booking => 
          selectedTimeSlot ? isSameSlot(booking.startTime, selectedTimeSlot.start) : false
        )}
        waitlistEntries={waitlistEntries.filter(entry => 
          selectedTimeSlot ? isSameSlot(entry.startTime, selectedTimeSlot.start) : false
        )} // Hinzugefügt
      />

      {/* Fehler- und Erfolgsmeldungen als Modal */}
      <ErrorModal
        isOpen={!!error || !!success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        title={error ? 'Fehler' : 'Erfolg'}
        message={error || success || 'Ein unerwarteter Fehler ist aufgetreten.'}
      />
    </div>
  );
};
